import { AdditionalServiceProduct } from "@omni/types/api/product/bff/shared/additionalServiceProduct.model";
import { ProductDocument } from "@omni/types/api/product/bff/shared/productDocument.model";
import { cn } from "@omni/utils/styles";
import { useCallback, useId } from "react";

import { Input } from "@/components/Form/Base/input.component";
import { Tooltip } from "@/components/General/Tooltip/tooltip.component";
import { TooltipType } from "@/components/General/Tooltip/tooltip.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { formatPrice } from "@/utils/price.utils";

type AdditionalServiceComponentProps = {
    additionalService: AdditionalServiceProduct;
    tooltip?: TooltipType;
    type?: "checkbox" | "static";
    removable?: boolean;
    addable?: boolean;
    showPrice?: boolean;
    onClick?: (additionalService: AdditionalServiceProduct) => void;
    isSelected?: boolean;
    documents?: Array<ProductDocument>;
};

const AdditionalServiceComponent = ({
    additionalService,
    tooltip,
    type = "static",
    removable = true,
    showPrice = false,
    onClick,
    isSelected,
    documents = [],
    addable = true
}: AdditionalServiceComponentProps) => {
    const { t } = useGranite();
    const isClientSide = useIsClientSide();
    const inputId = useId();

    const disabled = (!isSelected && !addable) || (isSelected && !removable);

    const handleOnServiceProductClick = () => onClick?.(additionalService);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const getPdpHref = useCallback(
        () =>
            isClientSide
                ? `${window.origin}/${Config.getLanguage()}/p/${
                      additionalService.sku
                  }`
                : undefined,
        [isClientSide]
    );

    const labelContent = (
        <>
            {t("product.additionalservice.title")}
            {showPrice && additionalService.price && (
                <span> | {formatPrice(additionalService.price)}</span>
            )}
            {type === "checkbox" && (
                <span className={"ml-1 small font-italic"}>
                    ({t("general.cartItem.serviceProduct.label.optional")})
                </span>
            )}
            {tooltip?.title && tooltip?.text && (
                <Tooltip
                    title={tooltip.title}
                    innerHTML={tooltip.text}
                />
            )}
        </>
    );

    return (
        <div className={cn({ "tw:text-grey-input": disabled })}>
            {type === "static" && (
                <p className={cn("tw:m-0 tw:mt-2")}>{labelContent}</p>
            )}
            {type === "checkbox" && (
                <div>
                    <Input
                        type="checkbox"
                        id={inputId}
                        name={additionalService.sku}
                        value={additionalService.sku}
                        checked={isSelected}
                        onChange={handleOnServiceProductClick}
                        disabled={disabled}
                        className={cn("tw:mr-1")}
                    />
                    <label
                        className={cn("m-0")}
                        htmlFor={inputId}
                    >
                        {labelContent}
                    </label>
                </div>
            )}
            <p className={cn("tw:text-sm tw:font-light")}>
                {t("general.cartItem.serviceProduct.supplierContact")}
                {isClientSide && (
                    <a
                        className={"tw:ml-1"}
                        href={getPdpHref()}
                    >
                        {t("general.cartItem.serviceProduct.moreInfo")}
                    </a>
                )}
            </p>
            {documents.map((doc, index) => (
                <p
                    key={`doc-${index}`}
                    className={cn("tw:text-sm tw:font-light")}
                >
                    <a
                        href={doc.url}
                        title={doc.altText || ""}
                        target={"_blank"}
                        rel={"noopener"}
                    >
                        {t("general.ecommerce.additionalServices.doc")}
                    </a>
                </p>
            ))}
        </div>
    );
};

export { AdditionalServiceComponent };
